import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  Autocomplete,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { Trash2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { nawyTheme as theme } from "@/styles/nawy-theme/nawy-theme";
import { DeveloperTrackItem, FormFactoryReadyApi } from "@/modules/common/types";
import { getDeveloperUsers } from "../api"; // Adjust import path if needed
import { getDevelopers } from "@/modules/leads/api/index.v2";
import { useOrganization } from "@/modules/common/context/organizations/get-user-organizations.provider";
import { queryCommonFlags } from "@/api/constants";
import { getFormFields } from "@/modules/lead-page/api/lead-page.v2.api";
import { transformFormData } from "@/modules/lead-page/utils";
import { FormFactory } from "@/modules/lead-page/components/lead-info/edit-fields/status/forms/form-factory/form-factory";
import { RowFormApi } from "../types";

interface CreateTrackRowProps {
  track: DeveloperTrackItem;
  index: number;
  onDelete: (id: string) => void;
  onChange: (id: string, developerUId: string, developerUserUId: string) => void;
  canDelete: boolean;
  errors?: {
    developer?: boolean;
    user?: boolean;
  };
  onRegisterFormApi: (id: string, api: RowFormApi | undefined) => void;
  onFormLoadingChange: (id: string, loading: boolean) => void;
}

const CreateTrackRow: React.FC<CreateTrackRowProps> = ({
  track,
  index,
  onDelete,
  onChange,
  canDelete,
  errors,
  onRegisterFormApi,
  onFormLoadingChange,
}) => {
  const [searchUsers, setSearchUsers] = useState("");
  const [searchDevelopers, setSearchDevelopers] = useState("");
  // Developers belong to one country's inventory. Sent once the organization has loaded; the CRM
  // resolves the country server-side and ignores this parameter whenever it can.
  const { selectedOrganizationCountry } = useOrganization();
  const [formApi, setFormApi] = useState<FormFactoryReadyApi | null>(null);

  // Fetch Developers locally for this row
  const { data: developersData, isLoading: isLoadingDevelopers } = useQuery({
    queryKey: ["developers", searchDevelopers, selectedOrganizationCountry],
    queryFn: () =>
      getDevelopers({
        ...(searchDevelopers && { name: searchDevelopers }),
        ...(selectedOrganizationCountry && {
          country: selectedOrganizationCountry,
        }),
      }),
    ...queryCommonFlags,
  });

  const developers = useMemo(
    () => developersData?.data?.results || [],
    [developersData]
  );

  // Fetch users for this specific row's chosen developer
  const { data: usersData, isLoading: isLoadingUsers } = useQuery({
    queryKey: ["developer-users", track.developerUId, searchUsers],
    queryFn: () =>
      getDeveloperUsers({
        developerUId: track.developerUId,
        search: searchUsers,
        excludeBlocked: true,
      }),
    enabled: !!track.developerUId,
    ...queryCommonFlags,
  });

  const users = usersData?.data?.results || [];

  const selectedDeveloper = useMemo(
    () => developers.find((d: any) => d.uid === track.developerUId) || null,
    [developers, track.developerUId]
  );

  const formUId = selectedDeveloper?.form?.uid;

  // Fetch the developer's linked presentation form definition, if any
  const { data: formFieldsQuery, isLoading: isLoadingFormFields } = useQuery({
    queryKey: ["getFormFields", formUId],
    queryFn: () => getFormFields(String(formUId)),
    enabled: !!formUId,
    staleTime: 30000,
  });

  const isLoadingRowForm = !!formUId && (isLoadingFormFields || !formFieldsQuery?.data);

  useEffect(() => {
    onFormLoadingChange(track.id, isLoadingRowForm);
  }, [track.id, isLoadingRowForm, onFormLoadingChange]);

  const handleFormReady = useCallback((api: FormFactoryReadyApi) => {
    setFormApi(api);
  }, []);
 
  useEffect(() => {
    if (!formUId) {
      onRegisterFormApi(track.id, undefined);
      return;
    }

    if (!formFieldsQuery?.data || !formApi) {
      // Form is required but not ready yet — block submission defensively.
      onRegisterFormApi(track.id, async () => ({ ok: false }));
      return;
    }

    const getRowFormResult: RowFormApi = async () => {
      const values = await formApi.getValidatedValues();
      if (!values) return { ok: false };

      const formData = transformFormData(values, formFieldsQuery);

      return { ok: true, formData };
    };

    onRegisterFormApi(track.id, getRowFormResult);

    return () => onRegisterFormApi(track.id, undefined);
  }, [track.id, formUId, formFieldsQuery, formApi, onRegisterFormApi]);

  const handleDeveloperSelect = (newValue: any) => {
    // If developer changes, we clear the user (the presentation form, if
     onChange(
      track.id,
      newValue?.uid || "",
      "" // Reset user
    );
    setSearchUsers("");
    setFormApi(null);
  };

  const handleUserSelect = (newValue: any) => {
    onChange(
      track.id,
      track.developerUId,
      newValue?.uid || ""
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: theme.nawy.spacing.md,
        position: "relative",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            color: theme.nawy.colors.semantic.text.primary,
          }}
        >
          Track {index + 1}
        </Typography>
        {canDelete && (
          <IconButton onClick={() => onDelete(track.id)} size="small">
            <Trash2 size={18} color={theme.nawy.colors.semantic.text.secondary} />
          </IconButton>
        )}
      </Box>

      {/* Developer Selector */}
      <Box>
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: 500,
            color: theme.nawy.colors.semantic.text.secondary,
            marginBottom: theme.nawy.spacing.xxs,
          }}
        >
          Developer
        </Typography>
        <FormControl sx={{ width: "100%" }}>
          <Autocomplete
            value={selectedDeveloper}
            onChange={(_, newValue) => {
              if (typeof newValue === 'string') return;
              handleDeveloperSelect(newValue);
            }}
            onInputChange={(_, newInputValue) => {
              setSearchDevelopers(newInputValue);
            }}
            options={developers}
            loading={isLoadingDevelopers}
            getOptionLabel={(option) => typeof option === 'string' ? option : option.name || ""}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select developer"
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor: "white",
                    ...(errors?.developer && {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#d32f2f",
                      },
                    }),
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {isLoadingDevelopers ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
            isOptionEqualToValue={(option, value) => option.uid === value?.uid}
            sx={{ width: "100%" }}
          />
        </FormControl>
      </Box>

      {/* User (Agent) Selector */}
      <Box>
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: 500,
            color: theme.nawy.colors.semantic.text.secondary,
            marginBottom: theme.nawy.spacing.xxs,
          }}
        >
          Agent
        </Typography>
        <FormControl sx={{ width: "100%" }}>
          <Autocomplete
            value={users.find(u => u.uid === track.developerUserUId) || null}
            onChange={(_, newValue) => {
              if (typeof newValue === 'string') return;
              handleUserSelect(newValue);
            }}
            onInputChange={(_, newInputValue) => {
              setSearchUsers(newInputValue);
            }}
            options={users}
            loading={isLoadingUsers}
            disabled={!track.developerUId}
            getOptionLabel={(option) => typeof option === 'string' ? option : option.name || ""}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={track.developerUId ? "Select agent (optional)" : "Select a developer first"}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor: "white",
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {isLoadingUsers && track.developerUId ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
            isOptionEqualToValue={(option, value) => option.uid === value?.uid}
            sx={{ width: "100%" }}
          />
        </FormControl>
      </Box>

      {/* Presentation form (mandatory, only rendered when the developer has one linked) */}
      {formUId && (
        <Box
          sx={{
            border: `1px solid ${theme.nawy.colors.semantic.border.primary}`,
            borderRadius: "8px",
            padding: theme.nawy.spacing.md,
          }}
        >
          {isLoadingRowForm ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : formFieldsQuery?.data ? (
            <FormFactory
             
              key={`${track.developerUId}-${formUId}`}
              submitFun={() => {}}
              formFields={formFieldsQuery.data}
              isLoadingSubmition={false}
              handleClose={() => {}}
              showComment={false}
              hideFooter
              onReady={handleFormReady}
            />
          ) : null}
        </Box>
      )}
    </Box>
  );
};

export default CreateTrackRow;
